import { Col, Row, Table, Tooltip } from "antd";
import { get } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  MdContentCopy,
  MdOpenInNew,
  MdOutlineDeleteOutline,
  MdOutlineLink,
  MdOutlinePlayCircle,
  MdOutlineStopCircle,
} from "react-icons/md";
import Pagination from "shared/components/Pagination";
import Status from "shared/components/Status";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { DATE_FORMAT } from "utils/constants";
import {
  SOURCE_STATUSES,
  SOURCE_STATUS_VARIANTS,
  sourceEndedByDate,
} from "../form";

const formatDate = (value) => (value ? moment(value).format(DATE_FORMAT) : "-");

const View = ({ data, count, setValues, skip, actions }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SOURCE);
  const LIST = t("LIST", { returnObjects: true });
  const STATUSES = t("STATUSES", { returnObjects: true });

  const columns = [
    {
      title: LIST.NAME,
      dataIndex: "name",
    },
    {
      title: LIST.PLATFORM,
      dataIndex: "platform",
      render: (platform) => platform || "-",
    },
    {
      title: LIST.PROCESS,
      dataIndex: "process",
      render: (process) => get(process, "name") || "-",
    },
    {
      title: LIST.START_DATE,
      dataIndex: "startDate",
      render: formatDate,
    },
    {
      title: LIST.END_DATE,
      dataIndex: "endDate",
      render: formatDate,
    },
    {
      title: LIST.STATUS,
      dataIndex: "status",
      render: (status) => (
        <Status
          status={SOURCE_STATUS_VARIANTS[status]}
          text={STATUSES[status] || "-"}
        />
      ),
    },
    {
      title: LIST.ACTIONS,
      dataIndex: "id",
      align: "right",
      render: (_, source) => {
        const isStopped = source.status === SOURCE_STATUSES.STOPPED;
        const isEnded =
          source.status === SOURCE_STATUSES.ENDED || sourceEndedByDate(source);
        const canRestart = isStopped && !sourceEndedByDate(source);
        return (
          <Row gutter={8} justify="end" align="center" wrap={false}>
            <Tooltip title={LIST.COPY_GUEST_LINK}>
              <Col onClick={() => actions.copyGuestLink(source)}>
                <MdContentCopy size={22} color={COLORS.C_PRIMARY} />
              </Col>
            </Tooltip>
            <Tooltip title={LIST.COPY_AUTHENTICATED_LINK}>
              <Col onClick={() => actions.copyAuthenticatedLink(source)}>
                <MdOutlineLink size={24} color={COLORS.C_PRIMARY} />
              </Col>
            </Tooltip>
            <Tooltip title={LIST.EDIT}>
              <Col onClick={() => actions.edit(source)}>
                <MdOpenInNew size={24} color={COLORS.C_PRIMARY} />
              </Col>
            </Tooltip>
            {canRestart ? (
              <Tooltip title={LIST.START}>
                <Col onClick={() => actions.start(source)}>
                  <MdOutlinePlayCircle size={24} color={COLORS.C_SUCCESS} />
                </Col>
              </Tooltip>
            ) : (
              <Tooltip title={isEnded ? LIST.STOP_ENDED : LIST.STOP}>
                <Col onClick={isEnded ? undefined : () => actions.stop(source)}>
                  <MdOutlineStopCircle
                    size={24}
                    color={isEnded ? COLORS.C_GRAY : COLORS.C_DANGER}
                  />
                </Col>
              </Tooltip>
            )}
            <Tooltip title={LIST.DELETE}>
              <Col onClick={() => actions.remove(source)}>
                <MdOutlineDeleteOutline size={24} color={COLORS.C_DANGER} />
              </Col>
            </Tooltip>
          </Row>
        );
      },
    },
  ];

  return (
    <>
      <Table
        dataSource={data}
        className="table__wrapper"
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ x: true }}
      />
      <Pagination count={count} setValues={setValues} skip={skip} />
    </>
  );
};

View.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  count: PropTypes.number,
  setValues: PropTypes.func.isRequired,
  skip: PropTypes.number,
  actions: PropTypes.shape({
    edit: PropTypes.func.isRequired,
    copyGuestLink: PropTypes.func.isRequired,
    copyAuthenticatedLink: PropTypes.func.isRequired,
    stop: PropTypes.func.isRequired,
    start: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
  }).isRequired,
};

View.defaultProps = {
  data: [],
  count: 0,
  skip: 0,
};

export default View;
